import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, UserCheck, Trash2 } from 'lucide-react';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { useDepartmentApprovers, useCreateDepartmentApprover, useUpdateDepartmentApprover, useDeleteDepartmentApprover } from '@/hooks/finance/useDepartmentApprovers';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';

const ApproversSettingsPage = () => {
  const { userRole } = useAuth();
  const isAdmin = userRole === 'admin';
  
  const { data: departments } = useDepartments();
  const { data: approvers, isLoading } = useDepartmentApprovers();
  const createApprover = useCreateDepartmentApprover();
  const updateApprover = useUpdateDepartmentApprover();
  const deleteApprover = useDeleteDepartmentApprover();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingApprover, setEditingApprover] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    department_id: '',
    user_id: '',
    max_approval_amount: '',
    is_active: true
  });

  // Fetch users for approver selection
  const { data: users } = useQuery({
    queryKey: ['user-profiles-for-approvers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, role')
        .in('role', ['admin', 'finance']);
      if (error) throw error;
      return data;
    }
  });

  const getUserDisplayName = (userId: string) => {
    const user = users?.find(u => u.id === userId);
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return userId.slice(0, 8) + '...';
  };

  const resetForm = () => {
    setFormData({
      department_id: '',
      user_id: '',
      max_approval_amount: '',
      is_active: true
    });
    setEditingApprover(null);
  };

  const openEditDialog = (approver: any) => {
    setEditingApprover(approver);
    setFormData({
      department_id: approver.department_id,
      user_id: approver.user_id,
      max_approval_amount: approver.max_approval_amount?.toString() || '',
      is_active: approver.is_active ?? true
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const data = {
      department_id: formData.department_id,
      user_id: formData.user_id,
      approval_level: 1, // Single-level approval system
      max_approval_amount: formData.max_approval_amount ? parseFloat(formData.max_approval_amount) : null,
      is_active: formData.is_active
    };

    try {
      if (editingApprover) {
        await updateApprover.mutateAsync({ id: editingApprover.id, ...data });
        toast.success('Approver updated successfully');
      } else {
        await createApprover.mutateAsync(data);
        toast.success('Approver added successfully');
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save approver');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to remove this approver?')) return;
    try {
      await deleteApprover.mutateAsync(id);
      toast.success('Approver removed successfully');
    } catch (error) {
      toast.error('Failed to remove approver');
    }
  };

  const getDepartmentName = (departmentId: string) => {
    return departments?.find(d => d.id === departmentId)?.name || 'Unknown';
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Department Approvers</h1>
            <p className="text-muted-foreground">Configure who can approve payment requests per department</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Approver
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingApprover ? 'Edit Approver' : 'Add Approver'}</DialogTitle>
                <DialogDescription>
                  {editingApprover ? 'Update approver settings' : 'Assign a user as an approver for a department'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments?.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user">User</Label>
                  <Select
                    value={formData.user_id}
                    onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users?.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name || user.last_name 
                            ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                            : user.id.slice(0, 8) + '...'
                          } ({user.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_amount">Max Amount (ETB)</Label>
                  <Input
                    id="max_amount"
                    type="number"
                    value={formData.max_approval_amount}
                    onChange={(e) => setFormData({ ...formData, max_approval_amount: e.target.value })}
                    placeholder="Unlimited"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createApprover.isPending || updateApprover.isPending}>
                    {editingApprover ? 'Update' : 'Add'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Configured Approvers
            </CardTitle>
            <CardDescription>
              {approvers?.length || 0} approvers configured across departments
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Department</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Max Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvers?.map((approver) => (
                    <TableRow key={approver.id}>
                      <TableCell className="font-medium">
                        {getDepartmentName(approver.department_id)}
                      </TableCell>
                      <TableCell>
                        {getUserDisplayName(approver.user_id)}
                      </TableCell>
                      <TableCell>
                        {approver.max_approval_amount 
                          ? `ETB ${approver.max_approval_amount.toLocaleString()}`
                          : 'Unlimited'
                        }
                      </TableCell>
                      <TableCell>
                        <Badge variant={approver.is_active ? 'default' : 'secondary'}>
                          {approver.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(approver)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(approver.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!approvers || approvers.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No approvers configured. Add your first approver.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ApproversSettingsPage;
